import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import VisualizationAccordion from '../components/VisualizationAccordion';

// Import visualization components
import LanguageNetwork from '../dashboard/LanguageNetwork';
import SequenceSunburst from '../dashboard/SequenceSunburst';
import BERTHeatmap from '../dashboard/BERTHeatmap';
import StickyNetworkGraph from '../dashboard/StickyNetworkGraph';
import CirclePack from '../dashboard/CirclePack';
import StickyDialogueGraph from '../dashboard/StickyDialogueGraph';
import ZoomableSunburst from '../dashboard/ZoomableSunburst';
import CharacterHeatmap from '../dashboard/CharacterHeatmap';
import ZoomableIcicle from '../dashboard/ZoomableIcicle';
import ChordDependency from '../dashboard/ChordDependency';
import BubbleChart from '../dashboard/BubbleChart';
import ZoomableTreemap from '../dashboard/ZoomableTreemap';

// Import data files
import internet_languages from '../data/dashboard/internet_languages.json';
import proust_ngrams from '../data/dashboard/proust_ngrams.json';
import bert_heatmap from '../data/dashboard/bert_heatmap.json';
import posnetwork from '../data/dashboard/posnetwork.json';
import totc from '../data/dashboard/totc.json';
import dialogue_network from '../data/dashboard/dialogue_network.json';
import full_lit_alphabetized_GPE from '../data/dashboard/full_lit_alphabetized_GPE.json';
import big_heatmap from '../data/dashboard/big_heatmap.json';
import american_realism_alphabetized_min_25 from '../data/dashboard/american_realism_alphabetized_min_25.json';
import emma_adjective_noun from '../data/dashboard/emma-adjective-noun.json';
import bubble_proust from '../data/dashboard/bubble_proust.json';
import full_lit_alphabetized_PERSON from '../data/dashboard/full_lit_alphabetized_PERSON.json';

const Visualizations = () => {
  // 200px accounts for page margins, accordion padding, and viewer padding
  const calcWidth = () => Math.min(window.innerWidth * 0.85, 900);

  const [dimensions, setDimensions] = useState({
    width: calcWidth(),
    height: 600,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: calcWidth(),
        height: 600,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const portfolio_data = [
    {
      title: 'AI and the Media Ecology of Linguistic Power',
      subtitle: 'Interactive network visualization of language representation across the internet',
      text: "AI training data largely comes from web scraping, which creates significant imbalances in language representation. This visualization connects languages based on similarity, with node size representing either web page count or speaker population. Blue nodes indicate underrepresented languages, orange shows overrepresented ones. The graphic illustrates how languages like English dominate internet content and subsequently influence AI model capabilities.",
      graph: (
        <LanguageNetwork
          height={dimensions.height}
          width={dimensions.width}
          data={internet_languages}
          undertitle=""
        />
      ),
      link: '/github-linguistic',
      button: 'See Code',
      interaction: 'Click on the nodes to drag and drop.',
    },
    {
      title: 'Chileno-GPT',
      subtitle: 'Fine-tuning GPT for Chilean Spanish dialect',
      text: "Chilean Spanish stands out among Spanish dialects for its unique slang and informal expressions. This project fine-tuned GPT on Chilean Reddit content to capture these linguistic patterns. As LLMs expand globally, incorporating regional dialects becomes increasingly important for authentic language generation and cultural representation.",
      button: 'See Code',
      link: '/github-chileno',
    },
    {
      title: 'Visualizing Transformers',
      subtitle: 'BERT attention heatmap demonstrating multi-headed attention mechanisms',
      text: 'Multi-headed attention powers modern NLP architectures. This visualization shows how BERT interprets token relationships across different attention layers, revealing how transformer models process semantic and syntactic information simultaneously—the key to their effectiveness.',
      graph: (
        <BERTHeatmap
          height={dimensions.height}
          width={dimensions.width}
          data={bert_heatmap}
        />
      ),
      interaction: 'Click the button to loop through the various attention layers.',
      link: '/github-bert-heatmap',
      button: 'See Code',
    },
    {
      title: 'ChatGPT for EdTech',
      subtitle: 'Automated homework generation using ChatGPT and BERT',
      text: 'This tool combines the ChatGPT API with BERT-based alternative generators to create customized fill-in-the-blank exercises. The system generates topic-specific content suitable for ESL instruction, demonstrating practical LLM applications in educational technology.',
      button: 'See Code',
      link: '/github-chatgpt',
    },
    {
      title: "America's Twitter Diplomacy Through Word Embeddings",
      subtitle: 'Network visualization of U.S. embassy communication patterns',
      text: "Word2Vec embeddings reveal how U.S. embassy Twitter accounts use distinct language when discussing different countries. The visualization maps these relationships in vector space, showing clear rhetorical differences between ally and adversary nations—a fascinating look at diplomatic communication strategies.",
      graph: (
        <StickyNetworkGraph
          height={dimensions.height}
          width={dimensions.width}
          data={posnetwork}
        />
      ),
      interaction: 'Click on the nodes to drag and drop.',
      link: '/github-social-science',
      button: 'See Code',
    },
    {
      title: 'Simpsons-GPT',
      subtitle: 'Episode generation using GPT fine-tuned on Simpsons scripts',
      text: "Fine-tuned on Simpsons script summaries, this GPT model generates original episodes from starter prompts. While the output isn't always coherent, the model successfully captures the show's distinctive comedic patterns and character voices—showcasing GPT's strength in learning stylistic nuances.",
      button: 'See Code',
      link: '/github-simpsons',
    },
    {
      title: 'Circle Packing with Charles Dickens',
      subtitle: 'Interactive hierarchical visualization of literary text analysis',
      text: "Using spaCy for linguistic analysis and D3 for visualization, this project breaks down 'A Tale of Two Cities' into its grammatical components. The interactive circle packing allows exploration of parts of speech and named entities, demonstrating scalable approaches to corpus analysis.",
      graph: <CirclePack height={dimensions.height} width={dimensions.width} data={totc} />,
      interaction: 'Click on the bubble to zoom.',
      link: '/github-literary',
      button: 'See Code',
    },
    {
      title: 'Machine Translation from Scratch with PyTorch',
      subtitle: "Neural machine translation implementation exploring core concepts",
      text: "This project builds a French-English translator using PyTorch, combining high-quality translations with larger noisy datasets to create a balanced 300k sample training set. The implementation provides hands-on exploration of sequence-to-sequence architectures and attention mechanisms.",
      button: 'See Code',
      link: '/github-french',
    },
    {
      title: 'Shakespearean Dialogue Networks',
      subtitle: 'Character interaction mapping from dramatic text',
      text: "This visualization converts raw text into network data showing character interactions in 'Romeo and Juliet'. The approach demonstrates how NLP techniques can quantify and visualize social dynamics within literary works, applicable to various text analysis scenarios.",
      graph: (
        <StickyDialogueGraph
          height={dimensions.height}
          width={dimensions.width}
          data={dialogue_network}
        />
      ),
      interaction: 'Click on the nodes to drag and drop.',
      link: '/github-literary',
      button: 'See Code',
    },
    {
      title: "BERT's CLS Vectors for Rhetorical Analysis",
      subtitle: 'Document-level embeddings for analyzing diplomatic communication',
      text: "BERT's CLS tokens aggregate text chunks into document-level embeddings, enabling analysis of entire Twitter streams. When projected to two dimensions, Russian and American embassy accounts cluster distinctly, with overlap in areas where diplomatic rhetoric aligns—revealing patterns in international communication.",
      button: 'See Code',
      link: '/github-bert-document',
    },
    {
      title: 'Sequences Sunburst for French N-Grams',
      subtitle: 'Common phrase patterns in French literature',
      text: "This visualization maps four-word sequences from Proust's 'Du côté de chez Swann', highlighting the most frequent French phrase patterns. The interactive sunburst makes it easy to explore linguistic structures common in literary French.",
      graph: (
        <SequenceSunburst
          height={dimensions.height}
          width={dimensions.width}
          data={proust_ngrams}
        />
      ),
      interaction: 'Hover over the outer edge to explore the most common n-grams.',
      link: '/github-literary',
      button: 'See Code',
    },
    {
      title: 'BERT for EdTech',
      subtitle: "Automated multiple choice distractor generation",
      text: "Creating plausible wrong answers for multiple choice questions is surprisingly time-intensive. This tool uses BERT to generate contextually appropriate alternatives, streamlining test creation for educators. Simple input of a sentence and correct answer produces a complete multiple choice question.",
      button: 'See Code',
      link: '/github-bert-ed',
    },
    {
      title: 'Named Entity Sunburst',
      subtitle: 'Geographic references across English literature',
      text: "A zoomable sunburst exploring place names mentioned throughout English literature. The visualization reveals which locations appear most frequently across different works, offering insights into the geographic imagination of literary tradition.",
      graph: (
        <ZoomableSunburst
          height={dimensions.height}
          width={dimensions.width}
          data={full_lit_alphabetized_GPE}
        />
      ),
      interaction: 'Click on a slice of the sunburst to zoom in for more detail.',
      link: '/github-literary',
      button: 'See Code',
    },
    {
      title: 'Economist-GPT',
      subtitle: "News article generation in The Economist's style",
      text: "Fine-tuned on The Economist's distinctive prose, this GPT model generates news articles with characteristic analytical tone and formal style. The project explores how language models can replicate publication-specific writing patterns and the implications for synthetic content creation.",
      button: 'See Code',
      link: '/github-economist',
    },
    {
      title: 'Orthographic Heatmap',
      subtitle: 'Character co-occurrence patterns in different languages',
      text: 'Character co-occurrence forms the foundation of language model understanding. These heatmaps visualize how characters combine across different languages, revealing phonetic patterns—like how Italian words frequently end in vowels. The statistical relationships shown here underpin natural language processing at its most fundamental level.',
      graph: (
        <CharacterHeatmap
          height={dimensions.height}
          width={dimensions.width}
          data={big_heatmap}
        />
      ),
      interaction: 'Click the button to change the language input.',
      link: '/github-orthographic',
      button: 'See Code',
    },
    {
      title: 'Ritmo: The Rhyming Search Engine',
      subtitle: 'Phonetic search using ML and deep learning',
      text: "Standard tokenization overlooks phonetic similarity. This rhyming search engine addresses that gap using both Word2Vec and fine-tuned BERT to find Spanish rhymes. The approach proves particularly valuable for ASR data augmentation pipelines and adapts readily to other languages.",
      button: 'See Code',
      link: '/github-ritmo',
    },
    {
      title: "Mark Twain's Vocabulary",
      subtitle: "Comprehensive lexical analysis of Twain's works",
      text: "This project categorizes Twain's complete vocabulary by parts of speech and named entities, organizing everything alphabetically for easy exploration. The icicle chart visualization enables dynamic investigation of lexical patterns across his entire oeuvre.",
      graph: (
        <ZoomableIcicle
          height={dimensions.height}
          width={dimensions.width}
          data={american_realism_alphabetized_min_25}
        />
      ),
      interaction: 'Click on a slice to explore greater detail.',
      link: '/github-literary',
      button: 'See Code',
    },
    {
      title: 'Keyword-to-Text with T5',
      subtitle: "Reverse summarization for educational content generation",
      text: 'T5 encoder-decoder architecture excels at generating full sentences from single keywords. This capability supports educational applications where students need to see vocabulary used in proper context, demonstrating practical applications of unsummarization techniques.',
      button: 'See Code',
      link: '/github-keyword',
    },
    {
      title: 'Visualizing Word Co-Occurrence with Chord Dependency Diagrams',
      subtitle: "Adjective-noun relationships in Austen's prose",
      text: "Different authors develop distinct stylistic patterns. This chord diagram maps how adjectives and nouns combine in 'Emma', revealing Austen's characteristic descriptive patterns and offering a quantitative approach to stylistic analysis.",
      graph: (
        <ChordDependency
          height={dimensions.height}
          width={dimensions.width}
          data={emma_adjective_noun}
        />
      ),
      interaction: '',
      link: '/github-literary',
      button: 'See Code',
    },
    {
      title: 'Dzongkha Romanization with LLMs',
      subtitle: 'Phonetic transcription for low-resource languages',
      text: "Dzongkha's highly unphonetic writing system presents challenges for romanization. Initial attempts with limited training data proved difficult, but proof-of-concept work with Thai demonstrates the approach's viability. The project highlights both the promise and challenges of applying LLMs to low-resource language tasks.",
      button: 'See Code',
      link: '/github-t5-dzongkha',
    },
    {
      title: 'Bubble Chart Dictionary',
      subtitle: 'Frequency-based French vocabulary learning',
      text: "This visualization takes the most common nouns from Proust's 'Du côté de chez Swann' and links each to its Wiktionary entry. The frequency-first approach provides an efficient path for language learners focusing on high-value vocabulary.",
      graph: (
        <BubbleChart
          height={dimensions.height}
          width={dimensions.width}
          data={bubble_proust}
        />
      ),
      interaction: 'Click on a bubble to link to Wiktionary.',
      link: '/github-literary',
      button: 'See Code',
    },
    {
      title: "Who's Who in English Literature",
      subtitle: 'Character distribution across literary canon',
      text: 'This zoomable treemap catalogs characters mentioned throughout English literature, showing their relative frequency across different works. The visualization offers a data-driven perspective on literary canon and character significance.',
      graph: (
        <ZoomableTreemap
          height={dimensions.height}
          width={dimensions.width}
          data={full_lit_alphabetized_PERSON}
        />
      ),
      interaction: 'Click on a section of the treemap to dive deeper.',
      link: '/github-literary',
      button: 'See Code',
    },
  ];

  return (
    <Layout>
      <div className="section">
        <div className="container">
          <div className="section-title">
            <h1>Language Technology & AI</h1>
            <p className="section-subtitle" style={{ marginBottom: '2rem' }}>
              Expertise in LLM integration, AI orchestration, and software engineering
            </p>
          </div>

          <div className="intro-text">
            <p>
              With extensive experience in natural language processing, machine learning, and AI system design, I specialize in helping organizations leverage the power of large language models and build sophisticated AI integrations. My expertise spans the full spectrum of language technology—from fine-tuning transformer models and building custom NLP pipelines to architecting production-grade AI systems and orchestrating complex multi-model workflows.
            </p>
            <p>
              Whether you need AI integration consulting, LLM optimization, custom model development, or end-to-end software engineering for language-based applications, I bring both deep technical knowledge and practical implementation experience. The projects below showcase some of my (non-proprietary) work across GPT fine-tuning, BERT applications, data visualization, and production ML systems.
            </p>
          </div>

          <VisualizationAccordion data={portfolio_data} />
        </div>
      </div>
    </Layout>
  );
};

export default Visualizations;
